import { useEffect, useMemo, useState } from "react";
import { Plus, Save, Zap, Pencil, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api-client";
import {
  useCreateCtaSequence,
  useCtaSequence,
  useTriggerNextCtaStep,
  useUpdateCtaSequence,
  type CtaSequence,
  type CtaStep,
  type CtaStepType,
} from "@/hooks/use-webinar-offers";

const STEP_LABELS: Record<CtaStepType, string> = {
  soft_mention: "Soft Mention",
  reveal: "Reveal",
  urgency: "Urgency",
  bonus_stack: "Bonus Stack",
  last_chance: "Last Chance",
  closed: "Closed",
};

const EMPTY_STEP: CtaStep = {
  stepType: "reveal",
  triggerAtSeconds: 0,
  config: {
    currency: "EUR",
    bonuses: [],
  },
};

type StepDraft = {
  stepType: CtaStepType;
  triggerAtSeconds: string;
  headline: string;
  description: string;
  productImageUrl: string;
  originalPriceCents: string;
  offerPriceCents: string;
  currency: string;
  countdownSeconds: string;
  scarcityCount: string;
  scarcityMode: "" | "real" | "simulated";
  ctaLabel: string;
  ctaUrl: string;
  bonuses: string;
};

function toStepDraft(step: CtaStep | null): StepDraft {
  return {
    stepType: step?.stepType ?? "reveal",
    triggerAtSeconds: step ? String(step.triggerAtSeconds) : "0",
    headline: step?.config.headline ?? "",
    description: step?.config.description ?? "",
    productImageUrl: step?.config.productImageUrl ?? "",
    originalPriceCents: step?.config.originalPriceCents !== undefined ? String(step.config.originalPriceCents) : "",
    offerPriceCents: step?.config.offerPriceCents !== undefined ? String(step.config.offerPriceCents) : "",
    currency: step?.config.currency ?? "EUR",
    countdownSeconds: step?.config.countdownSeconds !== undefined ? String(step.config.countdownSeconds) : "",
    scarcityCount: step?.config.scarcityCount !== undefined ? String(step.config.scarcityCount) : "",
    scarcityMode: step?.config.scarcityMode ?? "",
    ctaLabel: step?.config.ctaLabel ?? "",
    ctaUrl: step?.config.ctaUrl ?? "",
    bonuses: (step?.config.bonuses ?? [])
      .map((bonus) => `${bonus.name}:${bonus.valueCents}`)
      .join("\n"),
  };
}

function parseBonuses(raw: string) {
  return raw
    .split("\n")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [namePart, valuePart] = entry.split(":");
      return {
        name: namePart?.trim() ?? "",
        valueCents: Number(valuePart?.trim() ?? "0"),
      };
    })
    .filter((bonus) => bonus.name.length > 0 && Number.isFinite(bonus.valueCents));
}

function toStep(draft: StepDraft): CtaStep {
  return {
    stepType: draft.stepType,
    triggerAtSeconds: Number(draft.triggerAtSeconds || "0"),
    config: {
      headline: draft.headline.trim() || undefined,
      description: draft.description.trim() || undefined,
      productImageUrl: draft.productImageUrl.trim() || undefined,
      originalPriceCents: draft.originalPriceCents ? Number(draft.originalPriceCents) : undefined,
      offerPriceCents: draft.offerPriceCents ? Number(draft.offerPriceCents) : undefined,
      currency: draft.currency.trim() || "EUR",
      countdownSeconds: draft.countdownSeconds ? Number(draft.countdownSeconds) : undefined,
      scarcityCount: draft.scarcityCount ? Number(draft.scarcityCount) : undefined,
      scarcityMode: draft.scarcityMode || undefined,
      bonuses: parseBonuses(draft.bonuses),
      ctaLabel: draft.ctaLabel.trim() || undefined,
      ctaUrl: draft.ctaUrl.trim() || undefined,
    },
  };
}

export function CtaSequenceManager({ webinarId, compact = false }: { webinarId: string | undefined; compact?: boolean }) {
  const { data, isLoading, error, refetch } = useCtaSequence(webinarId);
  const primarySequence = useMemo(
    () => data?.find((sequence) => sequence.isActive) ?? data?.[0] ?? null,
    [data],
  );
  const [sequenceName, setSequenceName] = useState("Primary CTA Sequence");
  const [draftSteps, setDraftSteps] = useState<CtaStep[]>([]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [stepDraft, setStepDraft] = useState<StepDraft>(() => toStepDraft(null));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);

  const createSequence = useCreateCtaSequence(webinarId);
  const updateSequence = useUpdateCtaSequence(webinarId, primarySequence?.id);
  const triggerNextStep = useTriggerNextCtaStep(webinarId, primarySequence?.id);

  useEffect(() => {
    if (!primarySequence) {
      setSequenceName("Primary CTA Sequence");
      setDraftSteps([]);
      return;
    }

    setSequenceName(primarySequence.name);
    setDraftSteps(primarySequence.steps);
  }, [primarySequence]);

  const nextStep = primarySequence && primarySequence.lastTriggeredStepIndex + 1 < draftSteps.length
    ? draftSteps[primarySequence.lastTriggeredStepIndex + 1]
    : null;

  async function persistSequence() {
    if (!webinarId) {
      setSaveError("Missing webinar context");
      return;
    }

    if (draftSteps.length === 0) {
      setSaveError("Add at least one CTA step before saving.");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      if (primarySequence) {
        await updateSequence.mutate({
          name: sequenceName.trim() || "Primary CTA Sequence",
          steps: draftSteps,
          isActive: true,
        });
      } else {
        await createSequence.mutate({
          name: sequenceName.trim() || "Primary CTA Sequence",
          steps: draftSteps,
          isActive: true,
        });
      }

      refetch();
    } catch (error) {
      const message = error instanceof ApiError
        ? `${error.status}: ${error.message}`
        : error instanceof Error
          ? error.message
          : "Unable to save CTA sequence";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  }

  async function triggerNext() {
    if (!primarySequence) {
      setSaveError("Save the sequence before triggering steps.");
      return;
    }

    setIsTriggering(true);
    setSaveError(null);
    try {
      await triggerNextStep.mutate();
      refetch();
    } catch (error) {
      const message = error instanceof ApiError
        ? `${error.status}: ${error.message}`
        : error instanceof Error
          ? error.message
          : "Unable to trigger CTA step";
      setSaveError(message);
    } finally {
      setIsTriggering(false);
    }
  }

  function openEditor(index: number | null) {
    setEditingIndex(index);
    setStepDraft(toStepDraft(index === null ? EMPTY_STEP : draftSteps[index] ?? EMPTY_STEP));
    setIsEditorOpen(true);
  }

  function saveStep() {
    const nextStepDraft = toStep(stepDraft);
    const nextSteps = [...draftSteps];

    if (editingIndex === null) {
      nextSteps.push(nextStepDraft);
    } else {
      nextSteps[editingIndex] = nextStepDraft;
    }

    nextSteps.sort((left, right) => left.triggerAtSeconds - right.triggerAtSeconds);
    setDraftSteps(nextSteps);
    setIsEditorOpen(false);
    setEditingIndex(null);
  }

  function removeStep(index: number) {
    setDraftSteps((current) => current.filter((_, stepIndex) => stepIndex !== index));
  }

  if (isLoading) {
    return <div className="text-sm text-[var(--color-muted-foreground)]">Loading CTA sequence…</div>;
  }

  if (error) {
    return (
      <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
        {error}
        <button type="button" onClick={refetch} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`grid gap-3 ${compact ? "" : "md:grid-cols-[minmax(0,1fr)_auto]"}`}>
        <label className="grid gap-2 text-sm">
          <span className="font-medium text-[var(--color-foreground)]">Sequence name</span>
          <input
            value={sequenceName}
            onChange={(event) => setSequenceName(event.target.value)}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            placeholder="Primary CTA Sequence"
          />
        </label>

        <div className={`flex ${compact ? "flex-wrap" : "items-end"} gap-2`}>
          <button
            type="button"
            onClick={() => openEditor(null)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            <Plus className="h-4 w-4" /> Add CTA
          </button>
          <button
            type="button"
            onClick={() => void persistSequence()}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> Save Sequence
          </button>
          <button
            type="button"
            onClick={() => void triggerNext()}
            disabled={isTriggering || !primarySequence || !nextStep}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--color-accent-action)] px-3 py-2 text-sm font-medium text-[var(--color-accent-action-foreground)] disabled:opacity-60"
          >
            <Zap className="h-4 w-4" /> Trigger Next Step
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-[var(--color-foreground)]">Sequence timeline</p>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {primarySequence
                ? `Last triggered step: ${Math.max(primarySequence.lastTriggeredStepIndex + 1, 0)} / ${draftSteps.length}`
                : "Draft not saved yet"}
            </p>
          </div>
          {nextStep ? (
            <span className="rounded-full border border-[var(--color-primary-border)] bg-[var(--color-primary-light)] px-3 py-1 text-xs text-[var(--color-primary)]">
              Next: {STEP_LABELS[nextStep.stepType]} at {nextStep.triggerAtSeconds}s
            </span>
          ) : null}
        </div>

        {draftSteps.length === 0 ? (
          <div className="mt-4 rounded-xl border border-dashed border-[var(--color-border)] px-4 py-8 text-center text-sm text-[var(--color-muted-foreground)]">
            No CTA steps yet. Add the first CTA step to build the live offer sequence.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {draftSteps.map((step, index) => (
              <div key={`${step.stepType}-${step.triggerAtSeconds}-${index}`} className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--color-foreground)]">{STEP_LABELS[step.stepType]}</p>
                    <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                      Trigger: {step.triggerAtSeconds}s
                      {step.config.countdownSeconds ? ` • Countdown ${step.config.countdownSeconds}s` : ""}
                    </p>
                    <p className="mt-2 text-sm text-[var(--color-foreground)]">{step.config.headline ?? "Untitled CTA step"}</p>
                    {step.config.description ? (
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{step.config.description}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => openEditor(index)} className="rounded-lg border border-[var(--color-border)] p-2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]" aria-label="Edit CTA step">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => removeStep(index)} className="rounded-lg border border-[var(--color-border)] p-2 text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)]" aria-label="Remove CTA step">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {saveError ? (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {saveError}
        </div>
      ) : null}

      {isEditorOpen ? (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Step type</span>
              <select
                value={stepDraft.stepType}
                onChange={(event) => setStepDraft((current) => ({ ...current, stepType: event.target.value as CtaStepType }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              >
                {Object.entries(STEP_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Trigger time (seconds)</span>
              <input
                type="number"
                min="0"
                value={stepDraft.triggerAtSeconds}
                onChange={(event) => setStepDraft((current) => ({ ...current, triggerAtSeconds: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Headline</span>
              <input
                value={stepDraft.headline}
                onChange={(event) => setStepDraft((current) => ({ ...current, headline: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">CTA URL</span>
              <input
                value={stepDraft.ctaUrl}
                onChange={(event) => setStepDraft((current) => ({ ...current, ctaUrl: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm md:col-span-2">
              <span className="font-medium text-[var(--color-foreground)]">Description</span>
              <textarea
                value={stepDraft.description}
                onChange={(event) => setStepDraft((current) => ({ ...current, description: event.target.value }))}
                rows={3}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Offer price (cents)</span>
              <input
                type="number"
                min="0"
                value={stepDraft.offerPriceCents}
                onChange={(event) => setStepDraft((current) => ({ ...current, offerPriceCents: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Original price (cents)</span>
              <input
                type="number"
                min="0"
                value={stepDraft.originalPriceCents}
                onChange={(event) => setStepDraft((current) => ({ ...current, originalPriceCents: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Countdown (seconds)</span>
              <input
                type="number"
                min="0"
                value={stepDraft.countdownSeconds}
                onChange={(event) => setStepDraft((current) => ({ ...current, countdownSeconds: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Scarcity count</span>
              <input
                type="number"
                min="0"
                value={stepDraft.scarcityCount}
                onChange={(event) => setStepDraft((current) => ({ ...current, scarcityCount: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Product image URL</span>
              <input
                value={stepDraft.productImageUrl}
                onChange={(event) => setStepDraft((current) => ({ ...current, productImageUrl: event.target.value }))}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
            <label className="grid gap-2 text-sm md:col-span-2">
              <span className="font-medium text-[var(--color-foreground)]">Bonuses (`name:valueCents` per line)</span>
              <textarea
                value={stepDraft.bonuses}
                onChange={(event) => setStepDraft((current) => ({ ...current, bonuses: event.target.value }))}
                rows={3}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm"
              />
            </label>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setIsEditorOpen(false)} className="rounded-xl border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)]">
              Cancel
            </button>
            <button type="button" onClick={saveStep} className="rounded-xl bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-[var(--color-primary-foreground)]">
              Save CTA
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
